/**
 * Kalphaotalk 메인 애플리케이션 - URL 기반 라우팅
 * 경로: /login /register /rooms /friends /settings /chat/:roomId
 */
import { getToken, removeToken, apiLogout } from './lib/api';
import { getUserInfo, clearAll } from './lib/storage';
import { escapeHtml } from './lib/utils';
import { renderLoginPage } from './pages/login';
import { renderRegisterPage } from './pages/register';
import { renderRoomListPage } from './pages/room-list';
import { renderFriendsPage } from './pages/friends';
import { renderSettingsPage } from './pages/settings';
import { renderRoomInfoPage } from './pages/room-info';
import { enterChatRoom, leaveChatRoom } from './pages/chat-room';

type Page = 'login' | 'register' | 'rooms' | 'chat' | 'friends' | 'settings' | 'room-info';
export interface AppStateManager {
  navigateTo(page: Page, params?: Record<string, string>): void;
  getCurrentPage(): Page;
  getCurrentParams(): Record<string, string>;
}

/** 페이지 → URL 경로 매핑 */
const PAGE_PATH: Record<Page, string> = {
  login: '/login', register: '/register', rooms: '/rooms',
  friends: '/friends', settings: '/settings', chat: '/chat',
  'room-info': '/room-info',
};

/** URL 경로 → 페이지 매핑 */
function pathToPage(path: string): { page: Page; params: Record<string, string> } {
  if (path === '/login' || path === '/') return { page: 'login', params: {} };
  if (path === '/register') return { page: 'register', params: {} };
  if (path === '/rooms') return { page: 'rooms', params: {} };
  if (path === '/friends') return { page: 'friends', params: {} };
  if (path === '/settings') return { page: 'settings', params: {} };
  if (path === '/room-info') return { page: 'room-info', params: {} };
  const m = path.match(/^\/chat\/(.+)$/);
  if (m) return { page: 'chat', params: { roomId: m[1] } };
  return { page: 'login', params: {} };
}

let currentPage: Page = 'login';
let currentParams: Record<string, string> = {};

class App implements AppStateManager {
  private header = document.getElementById('app-header')!;
  private headerTitle = document.getElementById('header-title')!;
  private headerSubtitle = document.getElementById('header-subtitle')!;
  private headerUser = document.getElementById('header-user')!;
  private btnLogout = document.getElementById('btn-logout')!;
  private btnBack = document.getElementById('btn-back')!;
  private btnTheme = document.getElementById('btn-theme')!;
  private btnLeave = document.getElementById('btn-leave')!;
  private pageAuth = document.getElementById('page-auth')!;
  private pageRooms = document.getElementById('page-rooms')!;
  private pageChat = document.getElementById('page-chat')!;
  private pageFriends = document.getElementById('page-friends')!;
  private pageSettings = document.getElementById('page-settings')!;
  private pageRoomInfo = document.getElementById('page-room-info')!;

  constructor() {
    this.setupLogout();
    this.setupTheme();
    this.setupNav();
    this.setupKeyboardShortcuts();
    // 뒤로가기/앞으로가기 처리
    window.addEventListener('popstate', () => {
      const { page, params } = pathToPage(window.location.pathname);
      this.renderPage(page, params);
    });
  }

  private setupKeyboardShortcuts(): void {
    document.addEventListener('keydown', (e) => {
      // Ctrl+/ : 방 검색 포커스 (브라우저 주소창과 충돌 없는 조합)
      if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        const tag = document.activeElement?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;
        e.preventDefault(); const s = document.getElementById('room-search'); if (s) s.focus(); return;
      }
      // Ctrl+Shift+N : 새 채팅 (Ctrl+N은 새 창 열기와 충돌)
      if (e.ctrlKey && e.shiftKey && e.key === 'N') { e.preventDefault(); document.getElementById('btn-create-room')?.click(); return; }
      // Esc : 모달 닫기
      if (e.key === 'Escape') { const o = document.getElementById('modal-overlay'); if (o && !o.classList.contains('hidden')) o.click(); }
      // Alt+ArrowLeft : 뒤로가기 (입력창 포커스 없을 때만)
      if (e.altKey && e.key === 'ArrowLeft' && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
        if (this.getCurrentPage() === 'chat' || this.getCurrentPage() === 'room-info') { e.preventDefault(); document.getElementById('btn-back')?.click(); }
      }
    });
  }

  private setupTheme(): void {
    const btn = this.btnTheme, sun = document.getElementById('theme-icon-sun')!, moon = document.getElementById('theme-icon-moon')!;
    const update = () => { const d = document.documentElement.classList.contains('dark'); sun.classList.toggle('hidden', d); moon.classList.toggle('hidden', !d); };
    update();
    btn.addEventListener('click', () => {
      const d = document.documentElement.classList.toggle('dark');
      localStorage.setItem('kalpha_theme', d ? 'dark' : 'light');
      update();
      btn.style.transform = 'rotate(360deg)';
      setTimeout(() => btn.style.transform = '', 400);
      window.dispatchEvent(new CustomEvent('kalpha-theme-changed', { detail: { theme: d ? 'dark' : 'light' } }));
    });
  }

  private setupNav(): void {
    document.getElementById('nav-rooms')?.addEventListener('click', () => this.goAuth('rooms'));
    document.getElementById('nav-friends')?.addEventListener('click', () => this.goAuth('friends'));
    document.getElementById('nav-settings')?.addEventListener('click', () => this.goAuth('settings'));
  }

  private goAuth(page: Page): void { if (getToken()) this.navigateTo(page); }

  private updateNav(active: 'rooms' | 'friends' | 'settings'): void {
    const nav = document.getElementById('bottom-nav')!;
    nav.style.display = 'flex';
    ['nav-rooms','nav-friends','nav-settings'].forEach(id => {
      document.getElementById(id)!.style.color = id === `nav-${active}` ? 'var(--text-green)' : 'var(--text-tertiary)';
    });
  }
  private hideNav(): void { document.getElementById('bottom-nav')!.style.display = 'none'; }

  init(): void {
    const { page, params } = pathToPage(window.location.pathname);
    const t = getToken(); const ui = getUserInfo();
    if (t && ui) {
      if (page === 'login' || page === 'register') this.navigateTo('rooms');
      else this.navigateTo(page, params);
    } else {
      if (page !== 'login' && page !== 'register') this.navigateTo('login');
      else this.navigateTo(page, params);
    }
    // 최초 히스토리 상태 보정
    if (window.location.pathname !== PAGE_PATH[currentPage]) {
      history.replaceState(null, '', PAGE_PATH[currentPage]);
    }
  }

  navigateTo(page: Page, params?: Record<string, string>): void {
    if (currentPage === 'chat') leaveChatRoom();

    const path = page === 'chat' && params?.roomId ? `/chat/${params.roomId}` : PAGE_PATH[page];
    if (window.location.pathname !== path) history.pushState(null, '', path);

    this.renderPage(page, params || {});
  }

  private renderPage(page: Page, params: Record<string, string>): void {
    currentPage = page; currentParams = params;
    this.btnBack.classList.add('hidden'); this.btnBack.onclick = null;
    this.btnLogout.classList.add('hidden');
    this.btnTheme.classList.add('hidden'); this.btnLeave.classList.add('hidden'); this.btnLeave.onclick = null;
    this.headerSubtitle.classList.add('hidden'); this.headerUser.classList.add('hidden');
    this.headerTitle.classList.remove('hidden');
    const btnRoomInfo = document.getElementById('btn-room-info');
    if (btnRoomInfo) { btnRoomInfo.classList.add('hidden'); btnRoomInfo.onclick = null; }

    if (page === 'rooms' || page === 'friends' || page === 'settings') this.updateNav(page);
    else this.hideNav();

    const auth = ['rooms','chat','friends','settings','room-info'].includes(page);
    if (auth) { this.btnTheme.classList.remove('hidden'); this.header.classList.remove('hidden'); if (page !== 'chat' && page !== 'room-info') this.btnLogout.classList.remove('hidden'); }

    switch (page) {
      case 'login': this.headerTitle.textContent = 'Kalphaotalk'; this.header.classList.remove('hidden'); this.switchPage(this.pageAuth); renderLoginPage(document.getElementById('auth-container')!, this); break;
      case 'register': this.headerTitle.textContent = '회원가입'; this.header.classList.remove('hidden'); this.switchPage(this.pageAuth); renderRegisterPage(document.getElementById('auth-container')!, this); break;
      case 'rooms': { const u = getUserInfo(); if (!u) { this.navigateTo('login'); return; } this.headerTitle.textContent = 'Kalphaotalk'; this.headerUser.textContent = u.display_name; this.headerUser.classList.remove('hidden'); this.switchPage(this.pageRooms); renderRoomListPage(this.pageRooms, this); break; }
      case 'friends': { const u = getUserInfo(); if (!u) { this.navigateTo('login'); return; } this.headerTitle.textContent = '친구'; this.headerUser.textContent = u.display_name; this.headerUser.classList.remove('hidden'); this.switchPage(this.pageFriends); renderFriendsPage(this.pageFriends, this); break; }
      case 'settings': { const u = getUserInfo(); if (!u) { this.navigateTo('login'); return; } this.headerTitle.textContent = '설정'; this.headerUser.textContent = u.display_name; this.headerUser.classList.remove('hidden'); this.switchPage(this.pageSettings); renderSettingsPage(this.pageSettings, this); break; }
      case 'room-info': { const u = getUserInfo(); if (!u) { this.navigateTo('login'); return; } this.headerTitle.textContent = '방 정보'; this.btnBack.classList.remove('hidden'); this.btnBack.onclick = () => this.navigateTo('chat', { roomId: params.roomId || '' }); this.switchPage(this.pageRoomInfo); renderRoomInfoPage(this.pageRoomInfo, this, params.roomId || '', params.myRole || 'member'); break; }
      case 'chat': { const rid = params.roomId; if (!rid) { this.navigateTo('rooms'); return; } this.headerTitle.textContent = '채팅방'; this.btnBack.classList.remove('hidden'); this.switchPage(this.pageChat); enterChatRoom(rid, this); break; }
    }
  }

  private switchPage(target: HTMLElement): void {
    [this.pageAuth, this.pageRooms, this.pageChat, this.pageFriends, this.pageSettings, this.pageRoomInfo].forEach(p => {
      if (p === target) { p.classList.remove('hidden'); p.classList.add('animate-fade-in'); }
      else { p.classList.add('hidden'); p.classList.remove('animate-fade-in'); }
    });
  }

  getCurrentPage(): Page { return currentPage; }
  getCurrentParams(): Record<string, string> { return currentParams; }

  private setupLogout(): void {
    this.btnLogout.addEventListener('click', async () => { try { await apiLogout(); } catch {} removeToken(); clearAll(); if (currentPage === 'chat') leaveChatRoom(); this.navigateTo('login'); });
  }
}

(window as any).escapeHtml = escapeHtml;
document.addEventListener('DOMContentLoaded', () => { const app = new App(); app.init(); });
